import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import Logo from "@/components/torq/Logo";
import { scrollToId } from "@/components/torq/scrollTo";

const LINKS = [
  { id: "race", label: "THE RACE" },
  { id: "format", label: "FORMAT" },
  { id: "divisions", label: "DIVISIONS" },
  { id: "faq", label: "FAQ" },
];

const PAGE_LINKS = [
  { href: "/partners", label: "PARTNERS" },
  { href: "/officials", label: "OFFICIALS" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [citiesOpen, setCitiesOpen] = useState(false);
  const go = (id) => (e) => {
    if (e) e.preventDefault();
    setCitiesOpen(false);
    if (open) {
      // release the scroll lock before navigating so the target offset is valid
      document.documentElement.style.overflow = "";
      window.__lenis?.start();
    }
    setOpen(false);
    scrollToId(id)();
  };

  useEffect(() => {
    if (!open) return;
    document.documentElement.style.overflow = "hidden";
    window.__lenis?.stop();
    return () => {
      document.documentElement.style.overflow = "";
      window.__lenis?.start();
    };
  }, [open]);

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#161614]/85 border-b border-[#2e2e2a]" data-testid="header">
        <div className="flex items-center justify-between px-6 lg:px-12 h-16">
          <Logo />
          <nav className="hidden md:flex items-center gap-8" data-testid="header-links">
            {LINKS.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={go(l.id)}
                data-testid={`nav-link-${l.id}`}
                className="font-mono text-[11px] tracking-[0.25em] text-[#a3a39f] hover:text-[#f0eee4] transition-colors"
              >
                {l.label}
              </a>
            ))}
            {PAGE_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="hidden lg:inline font-mono text-[11px] tracking-[0.25em] text-[#a3a39f] hover:text-[#f0eee4] transition-colors"
              >
                {l.label}
              </a>
            ))}
            <div className="relative">
              <button
                onClick={() => setCitiesOpen((v) => !v)}
                data-testid="nav-cities-btn"
                className="font-mono text-[11px] tracking-[0.25em] text-[#a3a39f] hover:text-[#f0eee4] transition-colors inline-flex items-center gap-1.5"
              >
                CITIES
                <ChevronDown
                  size={12}
                  className={`transition-transform duration-300 ${citiesOpen ? "rotate-180" : ""}`}
                />
              </button>
              <AnimatePresence>
                {citiesOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setCitiesOpen(false)} />
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute right-0 top-9 z-50 w-64 bg-[#1e1e1c] border border-[#2e2e2a]"
                      data-testid="nav-cities-panel"
                    >
                      <p className="px-4 pt-3 pb-1 font-mono text-[10px] tracking-[0.25em] text-[#6e6e6a]">
                        UPCOMING EVENTS
                      </p>
                      <button
                        onClick={go("notify")}
                        data-testid="nav-city-delhi"
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#161614] transition-colors text-left"
                      >
                        <span className="font-display font-extrabold uppercase text-xl leading-none">
                          Delhi
                        </span>
                        <span className="font-mono text-[9px] tracking-[0.2em] text-[#a3a39f] border border-[#454540] px-2 py-1">
                          COMING SOON
                        </span>
                      </button>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </nav>
          <div className="flex items-center gap-3">
            <a
              href="/register"
              data-testid="header-notify-btn"
              className="btn-solid hidden sm:inline-flex bg-[#ff5a1f] text-[#0c0c0a] font-mono text-[11px] tracking-[0.2em] px-5 py-2.5 hover:bg-white"
            >
              REGISTER NOW
            </a>
            <button
              className="md:hidden p-3 min-w-[48px] min-h-[48px] flex items-center justify-center text-[#f0eee4]"
              onClick={() => setOpen(true)}
              data-testid="nav-menu-open"
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-[#161614] flex flex-col overflow-y-auto"
            data-testid="nav-mobile-menu"
          >
            <div className="flex items-center justify-between px-6 h-16 border-b border-[#2e2e2a]">
              <Logo />
              <button
                className="p-3 min-w-[48px] min-h-[48px] flex items-center justify-center text-[#f0eee4]"
                onClick={() => setOpen(false)}
                data-testid="nav-menu-close"
                aria-label="Close menu"
              >
                <X size={24} />
              </button>
            </div>
            <nav className="flex flex-col px-6 pt-8">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.id}
                  href={`#${l.id}`}
                  onClick={go(l.id)}
                  initial={{ y: 40, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.06 * i, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  data-testid={`mobile-nav-link-${l.id}`}
                  className="font-display font-extrabold uppercase text-3xl sm:text-4xl py-3 min-h-[48px] border-b border-[#2e2e2a] tracking-wide"
                >
                  {l.label}
                </motion.a>
              ))}
              {PAGE_LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  initial={{ y: 40, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.06 * (LINKS.length + i), duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className="font-display font-extrabold uppercase text-3xl sm:text-4xl py-3 min-h-[48px] border-b border-[#2e2e2a] tracking-wide"
                >
                  {l.label}
                </motion.a>
              ))}
              <motion.button
                onClick={() => setCitiesOpen((v) => !v)}
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.06 * (LINKS.length + PAGE_LINKS.length), duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                data-testid="mobile-cities-btn"
                className="font-display font-extrabold uppercase text-3xl sm:text-4xl py-3 min-h-[48px] border-b border-[#2e2e2a] tracking-wide text-left flex items-center justify-between"
              >
                CITIES
                <ChevronDown
                  size={26}
                  className={`transition-transform duration-300 ${citiesOpen ? "rotate-180" : ""}`}
                />
              </motion.button>
              <AnimatePresence>
                {citiesOpen && (
                  <motion.button
                    onClick={go("notify")}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    data-testid="mobile-city-delhi"
                    className="flex items-center justify-between py-4 pl-5 min-h-[52px] border-b border-[#2e2e2a] overflow-hidden"
                  >
                    <span className="font-display font-bold uppercase text-2xl">Delhi</span>
                    <span className="font-mono text-[9px] tracking-[0.2em] text-[#a3a39f] border border-[#454540] px-2 py-1">
                      UPCOMING EVENT
                    </span>
                  </motion.button>
                )}
              </AnimatePresence>
              <a
                href="#notify"
                onClick={go("notify")}
                data-testid="mobile-notify-btn"
                className="mt-10 btn-solid bg-[#ff5a1f] text-[#0c0c0a] text-center font-mono text-xs tracking-[0.2em] px-6 py-4 min-h-[52px] hover:bg-white"
              >
                GET NOTIFIED
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
